import { apiGet, apiPost } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface TaskProgress {
  current: number;
  total: number;
  pct: number;
  step?: string;
  /** Epoch seconds of the last progress update (running tasks). */
  last_progress_ts?: number;
}

export interface TaskHealth {
  status: 'alive' | 'lost' | 'unknown';
  last_heartbeat_ts?: string;
}

export interface TaskArtifact {
  name: string;
  s3_key: string;
  size: number;
  content_type: string;
  created_ts: string;
}

export interface TaskStep {
  name: string;
  label?: string;
  requires_approval?: boolean;
}

export interface BackgroundTaskRecord {
  id: string;
  task_type: string;
  task_label: string;
  state: number;
  state_display: string;
  visibility: number;
  visibility_display: string;
  created_ts: string;
  started_ts: string | null;
  finished_ts: string | null;
  updated_ts: string;
  duration_seconds: number | null;
  user_email: string | null;
  user_id: number | null;
  /** `environment`: the Tenant cell marks a sandbox. */
  tour_operator: { id: number; name: string; environment?: TourOperatorEnvironment | null } | null;
  /** Model spend recorded so far by an AI task (failed / cancelled runs
   *  included); null for tasks that record none. Absent on older backends. */
  cost_usd?: number | null;
  params: Record<string, unknown> | null;
  progress?: TaskProgress;
  health: TaskHealth | null;
  can_cancel: boolean;
  /** Awaiting approval and this surface may approve it — always so on the console (the
   *  operator dashboard refuses the easy-onboarding lane types). Absent on older backends. */
  can_approve?: boolean;
  can_reschedule: boolean;
  has_artifacts: boolean;
  artifacts: TaskArtifact[] | null;
  error_message: string;
}

export interface BackgroundTaskDetail extends BackgroundTaskRecord {
  steps: TaskStep[] | null;
  current_step: number;
  metrics: Record<string, unknown> | null;
  max_execution_time: number;
}

/** DB state constants (mirror BackgroundTaskRecord.STATE_*). */
export const TASK_STATE = {
  PENDING: 0,
  RUNNING: 1,
  COMPLETED: 2,
  FAILED: 3,
  CANCELLED: 4,
  AWAITING_APPROVAL: 5,
} as const;

export const ACTIVE_STATES: number[] = [
  TASK_STATE.PENDING, TASK_STATE.RUNNING, TASK_STATE.AWAITING_APPROVAL,
];

export function isActiveState(state: number): boolean {
  return ACTIVE_STATES.includes(state);
}

export interface BgTaskListParams {
  page?: number;
  page_size?: number;
  /** Restrict to tasks initiated by the current user (`?mine=true`). */
  mine?: boolean;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  state?: string;
  task_type?: string;
  task_label_contains?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  user?: string;
  visibility?: number;
  started_from?: string;
  started_to?: string;
  ordering?: string;
  /** One AI-diagnose thread: its anchor task plus every follow-up. A shared
   *  chat link carries only the anchor id, and the chat panel's own listing
   *  is paginated, so the thread has to be fetchable directly. */
  thread_task_id?: string;
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default — that omission is what keeps a cold first load free. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

function toQuery(p: BgTaskListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.mine) usp.set('mine', 'true');
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  if (p.state) usp.set('state', p.state);
  if (p.task_type) usp.set('task_type', p.task_type);
  if (p.count_mode) usp.set('count_mode', p.count_mode);
  if (p.count_only) usp.set('count_only', '1');
  if (p.task_label_contains) usp.set('task_label_contains', p.task_label_contains);
  if (p.from_date) usp.set('from_date', p.from_date);
  if (p.to_date) usp.set('to_date', p.to_date);
  if (p.search) usp.set('search', p.search);
  if (p.user) usp.set('user', p.user);
  if (p.visibility !== undefined) usp.set('visibility', String(p.visibility));
  if (p.started_from) usp.set('started_from', p.started_from);
  if (p.started_to) usp.set('started_to', p.started_to);
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.thread_task_id) usp.set('thread_task_id', p.thread_task_id);
  return usp.toString();
}

/** Total only, for the grid's on-demand row count (computed in Celery). */
export async function count(params: BgTaskListParams = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...params, count_only: true, count_mode: 'async' });
  return apiGet(`/bgtasks/?${qs}`);
}

export async function list(
  params: BgTaskListParams = {},
): Promise<PaginatedResponse<BackgroundTaskRecord>> {
  const qs = toQuery(params);
  return apiGet(`/bgtasks/${qs ? `?${qs}` : ''}`);
}

export async function retrieve(id: string): Promise<BackgroundTaskDetail> {
  return apiGet(`/bgtasks/${id}/`);
}

export async function activeCount(toId?: number | 'null'): Promise<{ active: number }> {
  const usp = new URLSearchParams();
  if (toId !== undefined) usp.set('tour_operator_id', String(toId));
  const qs = usp.toString();
  return apiGet(`/bgtasks/count/${qs ? `?${qs}` : ''}`);
}

export async function cancel(id: string): Promise<{ message: string }> {
  return apiPost(`/bgtasks/${id}/cancel/`);
}

export interface TaskTypeOption {
  value: string;
  label: string;
}

export async function taskTypes(): Promise<{ results: TaskTypeOption[] }> {
  return apiGet('/bgtasks/types/');
}

/** Approve the next step. `payload` is task-type-defined (e.g. ai_import
 *  passes the selected draft ids) and validated server-side; omit it for
 *  the legacy no-payload approval. */
export async function approve(
  id: string,
  payload?: Record<string, unknown>,
): Promise<{ message: string }> {
  return apiPost(`/bgtasks/${id}/approve/`, payload);
}

export interface TaskLogEntry {
  /** Redis stream ID (live entries only) — cursor for incremental fetches. */
  id?: string;
  ts?: string;
  levelname?: string;
  msg?: string;
  [key: string]: unknown;
}

export interface TaskLogsResponse {
  log_key: string;
  source: 'live' | 'archive' | null;
  last_id: string | null;
  entries: TaskLogEntry[];
}

/** Fetch log entries; pass `after` (the previous response's `last_id`) to
 *  receive only the diff — callers keep a local cache and append. */
export async function logs(id: string, after?: string): Promise<TaskLogsResponse> {
  const qs = after ? `?after=${encodeURIComponent(after)}` : '';
  return apiGet(`/bgtasks/${id}/logs/${qs}`);
}

export async function artifactUrl(
  id: string,
  name: string,
): Promise<{ url: string; expires_in: number }> {
  return apiGet(`/bgtasks/${id}/artifact_url/?name=${encodeURIComponent(name)}`);
}

/** An image artifact's bytes, streamed through the API. Show them from a `blob:` URL (see
 *  `useObjectUrls`): the console CSP's `img-src` allows no S3 host, so a presigned
 *  `artifactUrl` cannot be an `<img src>` in production — it is for downloads. 404 = unknown,
 *  missing or gone; 415 = not an image; 503 = storage failure. */
export async function artifactImage(id: string, name: string): Promise<Blob> {
  return apiGet<Blob>(`/bgtasks/${id}/artifact_image/?name=${encodeURIComponent(name)}`, {
    responseType: 'blob',
  });
}

export async function result(id: string): Promise<{ result: Record<string, unknown> }> {
  return apiGet(`/bgtasks/${id}/result/`);
}
